import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import { getApiErrorMessage } from '@/utils/apiError';
import { useCreateAccount, useUpdateAccount, useAccountTypes, useAccountColors } from '@/hooks/useAccounts';
import { ModalShell } from '@/components/ui/ModalShell.tsx';
import { FormField, ModalActions, SelectInput, TextInput } from '@/components/ui/primitives';

interface AccountData {
  name: string;
  account_type_id: number;
  billing_cycle_day?: number;
  payment_due_days?: number;
  credit_limit?: number;
  color_id: number | null;
}

interface Account {
  account_id: number;
  name: string;
  account_type_id: number;
  billing_cycle_day?: number;
  payment_due_days?: number;
  credit_limit?: number;
  color_id?: number | null;
}

interface NewAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  account?: Account | null;
}

export const NewAccountModal = ({ isOpen, onClose, onSuccess, account }: NewAccountModalProps) => {
  const { t } = useTranslation(['common', 'accounts']);
  // Use hooks
  const accountTypesQuery = useAccountTypes({ enabled: isOpen });
  const accountTypes = accountTypesQuery.data ?? [];
  const colorsQuery = useAccountColors({ enabled: isOpen });
  const accountColors = colorsQuery.data ?? [];
  const createAccountMutation = useCreateAccount();
  const updateAccountMutation = useUpdateAccount();

  const [error, setError] = useState<string | null>(null);
  const [didSubmit, setDidSubmit] = useState(false);
  const [formData, setFormData] = useState<AccountData>({
    name: '',
    account_type_id: 0,
    billing_cycle_day: undefined,
    payment_due_days: undefined,
    credit_limit: undefined,
    color_id: null,
  });

  const isEditMode = !!account;
  const isSubmitting = createAccountMutation.isPending || updateAccountMutation.isPending;

  // Populate form when editing
  useEffect(() => {
    if (account && isOpen) {
      setDidSubmit(false);
      setFormData({
        name: account.name || '',
        account_type_id: account.account_type_id || 0,
        billing_cycle_day: account.billing_cycle_day,
        payment_due_days: account.payment_due_days,
        credit_limit: account.credit_limit,
        color_id: account.color_id ?? null,
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        account_type_id: 0,
        billing_cycle_day: undefined,
        payment_due_days: undefined,
        credit_limit: undefined,
        color_id: null,
      });
      setError(null);
      setDidSubmit(false);
    }
  }, [account, isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, isSubmitting, onClose]);

  // Check if selected account type is credit
  const selectedAccountType = accountTypes.find(type => type.id === formData.account_type_id);
  const isCreditAccount = selectedAccountType?.name.toLowerCase() === 'credit';
  const accountNameError = didSubmit && !formData.name.trim();
  const accountTypeError = didSubmit && formData.account_type_id === 0;
  const creditLimitError = didSubmit && isCreditAccount && formData.credit_limit === undefined;
  const billingCycleError = didSubmit && isCreditAccount && (
    formData.billing_cycle_day === undefined || formData.billing_cycle_day < 1 || formData.billing_cycle_day > 31
  );
  const paymentDueError = didSubmit && isCreditAccount && (
    formData.payment_due_days === undefined || formData.payment_due_days < 1 || formData.payment_due_days > 60
  );
  const inlineError = (message: string) => (
    <span className="inline-flex items-center gap-2"><FiAlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />{message}</span>
  );

  const handleInputChange = (field: keyof AccountData, value: string | number) => {
    // If changing account type and switching away from credit, clear credit fields
    if (field === 'account_type_id') {
      const newTypeId = value as number;
      const newType = accountTypes.find(type => type.id === newTypeId);
      const willBeCredit = newType?.name.toLowerCase() === 'credit';

      if (!willBeCredit) {
        setFormData(prev => ({
          ...prev,
          account_type_id: newTypeId,
          billing_cycle_day: undefined,
          payment_due_days: undefined,
          credit_limit: undefined
        }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const handleIntegerChange = (field: 'billing_cycle_day' | 'payment_due_days', value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      const intValue = value === '' ? undefined : parseInt(value, 10);
      setFormData(prev => ({ ...prev, [field]: intValue }));
    }
  };

  const handleCreditLimitChange = (value: string) => {
    if (value === '' || /^\d*(\.\d{0,2})?$/.test(value)) {
      setFormData((prev) => ({ ...prev, credit_limit: value === '' ? undefined : Number(value) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDidSubmit(true);
    setError(null);

    // Validate account name
    if (!formData.name.trim()) {
      return;
    }

    // Validate account type
    if (formData.account_type_id === 0) {
      return;
    }

    // Validate credit fields if credit account
    if (isCreditAccount) {
      if (!formData.billing_cycle_day || !formData.payment_due_days || formData.credit_limit === undefined) {
        return;
      }

      // Validate ranges
      if (formData.billing_cycle_day < 1 || formData.billing_cycle_day > 31) {
        return;
      }

      if (formData.payment_due_days < 1 || formData.payment_due_days > 60) {
        return;
      }
    }

    // Prepare data to send
    const submitData: AccountData = {
      name: formData.name.trim(),
      account_type_id: formData.account_type_id,
      color_id: formData.color_id,
    };

    // Only include credit fields if they are provided and it's a credit account
    if (isCreditAccount) {
      submitData.billing_cycle_day = formData.billing_cycle_day;
      submitData.payment_due_days = formData.payment_due_days;
      submitData.credit_limit = formData.credit_limit;
    }

    try {
      if (isEditMode && account) {
        // Update existing account
        await updateAccountMutation.mutateAsync({
          id: account.account_id,
          data: submitData
        });
      } else {
        // Create new account
        await createAccountMutation.mutateAsync(submitData);
      }

      // Call success callback and close modal
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      setError(getApiErrorMessage(error, t('common:errors.generic')));
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="modal-title"
      title={isEditMode ? t('accounts.editTitle') : t('accounts.createTitle')}
      subtitle={isEditMode ? t('accounts.editSubtitle') : t('accounts.createSubtitle')}
      onSubmit={handleSubmit}
      noValidate
      mobileSheet
      variant="creation"
      maxWidthClassName="max-w-[560px]"
      panelClassName="account-modal-panel"
      disableClose={isSubmitting}
      footer={
        <div className="account-modal-actions flex w-full flex-col gap-3 sm:items-end">
          <ModalActions
            cancelLabel={t('common:actions.cancel')}
            submitLabel={isEditMode ? t('common:actions.save') : t('accounts.createTitle')}
            submittingLabel={isEditMode ? t('common:states.saving') : t('common:states.creating')}
            onCancel={onClose}
            isSubmitting={isSubmitting}
            isSubmitDisabled={isSubmitting}
          />
        </div>
      }
    >
      <div className="app-creation-form app-creation-form-grid account-creation-form mx-auto w-full">
            {/* Error Message */}
            {error && (
              <div className="app-creation-form-message rounded-xl border border-[color:color-mix(in_srgb,var(--app-danger)_28%,transparent)] bg-[var(--app-danger-soft)] p-3 text-sm text-[var(--app-danger)]">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="flex-1">{error}</p>
                </div>
              </div>
            )}

            <section className="app-creation-form-full account-creation-section">
              <h3 className="account-creation-section-title">{t('accounts.mainSection')}</h3>
              <div className="account-creation-fields">
                <FormField label={t('common:labels.name')} htmlFor="account-name" required errorId="account-name-error" error={accountNameError ? inlineError(t('accounts.accountNameRequired')) : undefined}>
                  <TextInput
                    id="account-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    disabled={isSubmitting}
                    autoFocus
                    aria-invalid={accountNameError}
                    aria-describedby={accountNameError ? 'account-name-error' : undefined}
                  />
                </FormField>

                <FormField
                  label={t('common:labels.type')}
                  htmlFor="account-type"
                  required
                  errorId="account-type-error"
                  error={accountTypesQuery.isError ? inlineError(t('common:errors.generic')) : accountTypeError ? inlineError(t('accounts.accountTypeRequired')) : undefined}
                >
                  <SelectInput
                    id="account-type"
                    value={formData.account_type_id}
                    onChange={(e) => handleInputChange('account_type_id', parseInt(e.target.value))}
                    className={formData.account_type_id === 0 ? 'text-[var(--app-muted-text)]' : ''}
                    data-placeholder={formData.account_type_id === 0}
                    required
                    disabled={isSubmitting}
                    aria-invalid={accountTypesQuery.isError || accountTypeError}
                    aria-describedby={accountTypesQuery.isError || accountTypeError ? 'account-type-error' : undefined}
                  >
                    <option value={0} disabled>{t('accounts.accountTypePlaceholder')}</option>
                    {accountTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name.charAt(0).toLocaleUpperCase() + type.name.slice(1).toLocaleLowerCase()}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
              </div>
            </section>

            <section className="app-creation-form-full account-creation-section">
              <h3 className="account-creation-section-title">{t('accounts.customizationSection')}</h3>
              <FormField label={t('accounts.colorLabel')} optionalLabel={t('common:labels.optional')} htmlFor="account-color" className="account-color-field">
                <fieldset className="min-w-0" disabled={isSubmitting}>
                  <legend className="sr-only">{t('accounts.colorLabel')}</legend>
                  {colorsQuery.isLoading ? (
                    <p role="status" className="text-sm text-[var(--ds-ink-muted)]">{t('accounts.colorsLoading')}</p>
                  ) : colorsQuery.isError ? (
                    <p role="status" className="text-sm text-[var(--ds-negative)]">{t('accounts.colorsLoadError')}</p>
                  ) : (
                    <div className="account-color-options" role="radiogroup" aria-describedby="account-color-help">
                      {accountColors.map((color) => (
                        <label key={color.id} className="account-color-option">
                          <input
                            id={color.id === accountColors[0]?.id ? 'account-color' : undefined}
                            className="peer sr-only"
                            type="radio"
                            name="account-color"
                            value={color.id}
                            checked={formData.color_id === color.id}
                            onClick={(event) => {
                              if (formData.color_id === color.id) {
                                event.preventDefault();
                                setFormData((current) => ({ ...current, color_id: null }));
                              }
                            }}
                            onChange={() => setFormData((current) => ({ ...current, color_id: color.id }))}
                            aria-label={t(`accounts.colorNames.${color.name}`, { defaultValue: color.name })}
                          />
                          <span
                            aria-hidden="true"
                            className="account-color-swatch"
                            style={{ backgroundColor: color.hex }}
                          >
                            {formData.color_id === color.id ? <FiCheck className="h-5 w-5" /> : null}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  <p id="account-color-help" className="mt-2 text-sm text-[var(--ds-ink-muted)]">{t('accounts.colorHelp')}</p>
                </fieldset>
              </FormField>
            </section>

            {/* Credit Card Specific Fields - Conditional Section. Below lg the card
                flattens so its fields keep the same edge, gap and two-column rhythm as
                the rest of the sheet. */}
            {isCreditAccount && (
              <div className="app-creation-form-full rounded-xl border border-transparent bg-transparent p-0 md:mt-6 md:border-[var(--app-border)] md:bg-[var(--app-surface-soft)] md:p-5">
                <div className="mb-2 md:mb-4">
                  <h3 className="mb-1 text-xs font-semibold text-[var(--app-text)] md:text-sm">
                    {t('accounts.creditDetailsTitle')}
                  </h3>
                  <p className="hidden text-xs text-[var(--app-muted-text)] md:block">
                    {t('accounts.creditDetailsHelp')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 md:gap-4">
                  <FormField
                    label={t('accounts.creditLimitLabel')}
                    htmlFor="credit-limit"
                    required
                    helper={t('accounts.creditLimitHelp')}
                    errorId="credit-limit-error"
                    error={creditLimitError ? inlineError(t('accounts.creditFieldsRequired')) : undefined}
                  >
                    <TextInput
                      id="credit-limit"
                      type="text"
                      inputMode="decimal"
                      value={formData.credit_limit !== undefined ? formData.credit_limit : ''}
                      onChange={(e) => handleCreditLimitChange(e.target.value)}
                      placeholder="25000"
                      required
                      disabled={isSubmitting}
                      aria-invalid={creditLimitError}
                      aria-describedby={creditLimitError ? 'credit-limit-error' : undefined}
                    />
                  </FormField>
                  {/* Billing Cycle Day */}
                  <FormField
                    label={t('accounts.billingCycleDayLabel')}
                    htmlFor="billing-cycle"
                    required
                    helper={t('accounts.billingCycleDayHelp')}
                    errorId="billing-cycle-error"
                    error={billingCycleError ? inlineError(formData.billing_cycle_day === undefined ? t('accounts.creditFieldsRequired') : t('accounts.billingCycleDayRange')) : undefined}
                  >
                    <TextInput
                      id="billing-cycle"
                      type="text"
                      inputMode="numeric"
                      value={formData.billing_cycle_day !== undefined ? formData.billing_cycle_day : ''}
                      onChange={(e) => handleIntegerChange('billing_cycle_day', e.target.value)}
                      placeholder="15"
                      required
                      disabled={isSubmitting}
                      aria-invalid={billingCycleError}
                      aria-describedby={billingCycleError ? 'billing-cycle-error' : undefined}
                    />
                  </FormField>

                  {/* Payment Due Days */}
                  <FormField
                    label={t('accounts.paymentDueDaysLabel')}
                    htmlFor="payment-due"
                    required
                    helper={t('accounts.paymentDueDaysHelp')}
                    errorId="payment-due-error"
                    error={paymentDueError ? inlineError(formData.payment_due_days === undefined ? t('accounts.creditFieldsRequired') : t('accounts.paymentDueDaysRange')) : undefined}
                  >
                    <TextInput
                      id="payment-due"
                      type="text"
                      inputMode="numeric"
                      value={formData.payment_due_days !== undefined ? formData.payment_due_days : ''}
                      onChange={(e) => handleIntegerChange('payment_due_days', e.target.value)}
                      placeholder="20"
                      required
                      disabled={isSubmitting}
                      aria-invalid={paymentDueError}
                      aria-describedby={paymentDueError ? 'payment-due-error' : undefined}
                    />
                  </FormField>
                </div>
              </div>
            )}
      </div>
    </ModalShell>
  );
};
